import React, { useMemo, useState } from 'react';
import Button from './Button/Button';
import Modal from './Modal/Modal';
import CancelCreditFunding from './CancelCreditFunding';
import alert from '../helpers/alert';
import extractErrorMessage from '../utils/extractErrorMessage';
import cancelCreditAppeal from '../api/creditPlatform/forte/cancelCreditAppeal';
import berekeManualReject from '../api/bank/berekeManualReject';
import { BANKS, BANKS_OPTIONS, DEAL_STEPS } from '../constants';
import {
  BANK_CREDIT_STATUSES,
  BANK_CREDIT_CANCELLED,
  BANK_CREDIT_REJECTED,
} from '../constants/bankCreditStatuses';

// Банки, у которых на бэке реализована отмена заявки
// (диспетчер cancelCreditAppeal: Euraz/БЦК/Halyk/Forte/Sapa/PledgeCar; Bereke — отдельный эндпоинт)
// BANKS.BEREKE временно отключён — банк вернётся с доработкой ручного отказа.
// Чтобы вернуть: добавить BANKS.BEREKE обратно в массив (эндпоинт/метод сохранены).
const CANCEL_SUPPORTED = [BANKS.EURAZ, BANKS.BCK, BANKS.HALYK, BANKS.FORTE, BANKS.SAPA, BANKS.PLEDGECAR];

const bankMeta = (id) => BANKS_OPTIONS.find((b) => b.value === id) || {};

// Есть ли у заявки идентификатор для отмены
const hasCancelId = (a) =>
  a?.bank?.id === BANKS.BEREKE ? a?.bankUniqueId || a?.bankOwnerId : a?.bankOwnerId;

const BankAppealsCancelBlock = ({ creditAppeals, deal, update }) => {
  const [confirmAppeal, setConfirmAppeal] = useState(null);
  const [loading, setLoading] = useState(false);

  // Финансирование Евраза уже выдано → нужен возврат ТС (другой поток)
  const isEurazFinanced = deal?.step === DEAL_STEPS.FINISH;

  const cancelableAppeals = useMemo(() => {
    return (creditAppeals || []).filter(
      (a) =>
        CANCEL_SUPPORTED.includes(a?.bank?.id) &&
        hasCancelId(a) &&
        a?.statusId !== BANK_CREDIT_CANCELLED &&
        a?.statusId !== BANK_CREDIT_REJECTED
    );
  }, [creditAppeals]);

  if (!cancelableAppeals.length) return null;

  const doCancel = async (appeal) => {
    setLoading(true);
    try {
      if (appeal?.bank?.id === BANKS.BEREKE) {
        await berekeManualReject(appeal?.bankUniqueId || appeal?.bankOwnerId);
      } else {
        await cancelCreditAppeal(appeal?.id);
      }
      alert.success('Заявка успешно отменена');
      setConfirmAppeal(null);
      if (update) update();
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Ошибка при отмене заявки');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: 20, border: '1px solid #E0E0E0', borderRadius: 8, padding: 16 }}>
      <p style={{ fontWeight: 600, marginBottom: 12 }}>Отмена заявок по банкам</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {cancelableAppeals.map((appeal) => {
          const meta = bankMeta(appeal?.bank?.id);
          const name = appeal?.bank?.name || meta.label || 'Банк';
          const isEuraz = appeal?.bank?.id === BANKS.EURAZ;
          return (
            <div
              key={appeal?.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
                paddingBottom: 8,
                borderBottom: '1px solid #F0F0F0',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 200 }}>
                {meta.image ? (
                  <img
                    src={meta.image}
                    alt={name}
                    style={{ height: 24, width: 'auto', objectFit: 'contain' }}
                  />
                ) : null}
                <span style={{ fontWeight: 500 }}>{name}</span>
                <span style={{ color: '#888', fontSize: 13 }}>
                  {BANK_CREDIT_STATUSES[appeal?.statusId] || ''}
                </span>
              </div>

              {isEuraz && isEurazFinanced ? (
                <CancelCreditFunding
                  dealId={deal?.id}
                  bankOwnerId={appeal?.bankOwnerId}
                  chunkId={deal?.chunkId}
                  buttonText="Отменить заявку и финансирование"
                />
              ) : (
                <Button fill onClick={() => setConfirmAppeal(appeal)}>
                  Отменить заявку
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <Modal
        isOpen={!!confirmAppeal}
        onRequestClose={() => (loading ? null : setConfirmAppeal(null))}
        contentStyle={{ maxWidth: 420, bottom: 'auto', height: 'auto', borderRadius: 10 }}
        title="Подтвердите отмену заявки"
      >
        <p style={{ marginBottom: 24, fontSize: 15 }}>
          Отменить заявку в банке{' '}
          <b>{confirmAppeal?.bank?.name || bankMeta(confirmAppeal?.bank?.id).label}</b>?
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button
            disabled={loading}
            onClick={() => setConfirmAppeal(null)}
            style={{ border: '1px solid #B45436', background: '#fff', color: '#B45436', padding: '12px 28px' }}
          >
            Нет
          </Button>
          <Button fill disabled={loading} onClick={() => doCancel(confirmAppeal)}>
            {loading ? 'Отмена…' : 'Да, отменить'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default BankAppealsCancelBlock;
